<template>
  <ColumnChart
    ref="chart"
    :unit="unit"
    :scale="scale"
    :legend="true"
    title="Shift (%)"
    :loading="loading"
    :daterange="daterange"
  />
</template>

<script>
import {
  mapState,
  mapGetters,
  mapActions,
  mapMutations,
} from 'vuex';

import ColumnChart from '../ColumnChart.vue';

export default {
  name: 'OEEShiftwise',
  props: {
    scale: { type: Number, default: 1 },
    unit: { type: String, default: '' },
  },
  components: {
    ColumnChart,
  },
  data() {
    return {
      daterange: '',
      loading: false,
      machinenames: [],
      plotLines: [
        {
          value: 0,
          color: '#72E7B2',
          dashStyle: '',
          width: 2,
          label: {
            rotation: 0, text: '80', x: -5, y: -2, style: { fontSize: 8, color: '#fff' },
          },
        },
        {
          value: 0,
          color: '#FFDA6B',
          dashStyle: '',
          width: 2,
          label: {
            rotation: 0, text: '60', x: -5, y: -2, style: { fontSize: 8, color: '#fff' },
          },
        },
      ],
    };
  },
  computed: {
    ...mapState('cellwiseProductionDashboard', [
      'shifts', 'machines', 'serverTime', 'dataFromJSON', 'config', 'theme', 'userId', 'customerId', 'currentShift', 'currentDate',
    ]),
    ...mapGetters('cellwiseProductionDashboard', ['firstShift', 'activeTheme']),
    cellname() {
      return this.$route.query.selectedcell;
    },
  },
  watch: {
    '$route.query.selectedcell': {
      async handler() {
        if (!this.dataFromJSON) {
          await this.fetchData();
        } else if (this.dummydata) {
          await this.dummydata(this.lastdays ? -1 : undefined);
        }
      },
    },
    async currentDate() {
      if (!this.dataFromJSON) {
        this.loading = false;
        await this.fetchData();
      }
    },
  },
  async mounted() {
    if (this.dataFromJSON) {
      await this.dummydata();
    } else {
      await this.fetchData();
    }
  },
  methods: {
    ...mapActions('cellwiseProductionDashboard', [
      'allShifts', 'getMachines', 'getReports', 'getFileData',
    ]),
    ...mapMutations('cellwiseProductionDashboard', ['setShifts']),

    getBusinessDate(value) {
      let dateObj = null;
      if (value !== null && value !== undefined) {
        const raw = typeof value === 'string' ? value.trim() : value;
        if (typeof raw === 'number' || /^\d+$/.test(String(raw))) {
          const num = Number(raw);
          const rawStr = String(raw);
          if (rawStr.length === 8) {
            const year = parseInt(rawStr.substring(0, 4), 10);
            const month = parseInt(rawStr.substring(4, 6), 10) - 1;
            const day = parseInt(rawStr.substring(6, 8), 10);
            dateObj = new Date(year, month, day);
          } else {
            const parsed = new Date(num);
            if (!Number.isNaN(parsed.getTime())) dateObj = parsed;
          }
        } else {
          const parsed = new Date(raw);
          if (!Number.isNaN(parsed.getTime())) dateObj = parsed;
        }
      }
      if (!dateObj) dateObj = new Date();
      dateObj.setHours(0, 0, 0, 0);
      return dateObj;
    },

    toYYYYMMDD(dateObj) {
      return parseInt(
        `${dateObj.getFullYear()}${String(dateObj.getMonth() + 1).padStart(2, '0')}${String(dateObj.getDate()).padStart(2, '0')}`,
        10,
      );
    },

    async dummydata() {
      try {
        const shifts = await this.getFileData('jsondata/shifts.json');
        if (shifts) this.setShifts(shifts);
        const res = await this.getFileData('jsondata/oeeshiftwise.json');
        if (res) {
          this.machinenames = res.machinenames || [];
          const date = new Date().getTime() - 86400000;
          this.setTitle(date);
          this.setOptions(res.reportdata);
        }
      } catch (error) {
        this.setZeroState();
      }
    },
    async fetchData() {
      if (this.loading) return;
      this.loading = true;

      try {
        const businessDate = this.getBusinessDate(this.currentDate || this.serverTime);
        const targetDate = new Date(businessDate);
        targetDate.setDate(targetDate.getDate() - 1);
        this.setTitle(targetDate.getTime());

        const yyyymmddDate = this.toYYYYMMDD(targetDate);

        if (!this.machines || this.machines.length === 0) {
          await this.getMachines();
        }
        let filteredMachines = this.machines || [];
        if (this.cellname) {
          filteredMachines = filteredMachines.filter((m) => m.machinecell === this.cellname);
        }
        this.machinenames = filteredMachines.length > 0
          ? filteredMachines.map((m) => m.machinename).sort()
          : ['-'];

        let activeSite = this.$store.state.user?.activeSite
         || this.$store.state.auth?.user?.site_id;

        if (!activeSite) {
          try {
            const swxUser = JSON.parse(localStorage.getItem('user') || '{}');
            activeSite = swxUser?.site_id || swxUser?.site?.id || swxUser?.customer?.site_id;
          } catch (e) { /* ignore */ }
        }

        if (!activeSite && this.machines?.length > 0) {
          activeSite = this.machines[0].siteId || this.machines[0].siteid;
        }

        if (!activeSite) {
          this.setZeroState();
          this.loading = false;
          return;
        }

        const payload = {
          siteid: activeSite,
          start: yyyymmddDate,
          end: yyyymmddDate,
          singledaterange: false,
          all: true,
        };

        const reportName = 'shiftwisemachineperformance';
        const result = await this.getReports({ reportName, payload });
        if (result) {
          const parsedResult = typeof result === 'string' ? JSON.parse(result) : result;
          let finalData = parsedResult.reportData || parsedResult;

          if (typeof finalData === 'string' && finalData !== '') {
            finalData = JSON.parse(finalData);
          }

          if (finalData && !Array.isArray(finalData) && finalData.machines) {
            finalData = finalData.machines;
          }

          if (finalData && finalData.length > 0) {
            this.setOptions(finalData);
          } else {
            this.setZeroState();
          }
        } else {
          this.setZeroState();
        }
      } catch (e) {
        this.setZeroState();
      } finally {
        this.loading = false;
      }
    },
    setOptions(data) {
      if (!data || !Array.isArray(data)) return;

      const filtershifts = this.shifts ? this.shifts.filter((s) => s.type === 'shift') : [];
      const sortedShift = filtershifts.sort((a, b) => a.sortindex - b.sortindex);
      let shiftnames = sortedShift.map((s) => s.shift || s.shiftname).filter(Boolean);

      if (shiftnames.length === 0) {
        shiftnames = [...new Set(data.map((d) => d.shift
         || d.shiftName || d.shiftname))].filter(Boolean);
      }

      if (shiftnames.length === 0) {
        shiftnames = [this.$t('dashboard.shift1'), this.$t('dashboard.shift2'), this.$t('dashboard.shift3')];
      }

      const uniqueShifts = [...new Set(shiftnames)];
      const colors = ['#21CE99', '#FFDA6B', '#627EEA', '#B6A481', '#EB7D53'];

      const series = uniqueShifts.map((s, i) => ({
        color: colors[i % colors.length],
        name: s,
        data: [],
      }));

      // Map OEE to all machines in the cell
      uniqueShifts.forEach((shift, shiftIndex) => {
        this.machinenames.forEach((machine) => {
          let oee = 0;
          data.forEach((d) => {
            const rowShift = d.shift || d.shiftName || d.shiftname;
            if ((d.machinename === machine || d.machinecode === machine) && rowShift === shift) {
              oee = d.oee || d.OEE || 0;
            }
          });
          const oeeinper = Number.isNaN(oee) ? 0 : Math.round(oee * 100) / 100;
          series[shiftIndex].data.push(oeeinper);
        });
      });

      let finalCategories = [];
      let finalSeries = [];

      if (this.machinenames.length === 0 || (this.machinenames.length === 1 && this.machinenames[0] === '-')) {
        finalCategories = [this.$t('dashboard.noProduction')];
        finalSeries = uniqueShifts.map((s, i) => ({
          color: colors[i % colors.length], name: s, data: [0],
        }));
      } else {
        finalCategories = this.machinenames;
        finalSeries = series;
      }

      this.applyPlotLinesAndRender(finalSeries, finalCategories);
    },
    setZeroState() {
      const filtershifts = this.shifts ? this.shifts.filter((s) => s.type === 'shift') : [];
      const sortedShift = filtershifts.sort((a, b) => a.sortindex - b.sortindex);
      let shiftnames = [...new Set(sortedShift.map((s) => s.shift || s.shiftname))].filter(Boolean);

      if (shiftnames.length === 0) {
        shiftnames = [this.$t('dashboard.shift1'), this.$t('dashboard.shift2'), this.$t('dashboard.shift3')];
      }

      const colors = ['#21CE99', '#FFDA6B', '#627EEA', '#B6A481', '#EB7D53'];
      const series = shiftnames.map((s, i) => ({
        color: colors[i % colors.length],
        name: s,
        data: this.machinenames && this.machinenames.length > 0
          ? this.machinenames.map(() => 0)
          : [0],
      }));

      this.applyPlotLinesAndRender(series);
    },
    applyPlotLinesAndRender(series) {
      let lablecolor = '#FFFFFF';
      if (this.theme && this.theme.themes && this.activeTheme) {
        lablecolor = this.theme.themes[this.activeTheme].titlecolor;
      }

      const plotLines = [...this.plotLines];
      const greenVal = this.config.greaterthangreen || 80;
      const redVal = this.config.lessthanred || 60;

      plotLines[0].value = greenVal;
      plotLines[0].label.text = String(greenVal);
      plotLines[0].label.style.color = lablecolor;
      plotLines[1].value = redVal;
      plotLines[1].label.text = String(redVal);
      plotLines[1].label.style.color = lablecolor;

      const plotOptions = {
        series: {
          borderWidth: 0,
          pointPadding: 0,
          groupPadding: 0.05,
          dataLabels: {
            enabled: false,
            style: { textOutline: 'none' },
          },
        },
      };

      if (this.$refs.chart) {
        this.$refs.chart.assignData({
          series,
          plotLines,
          plotOptions,
          charttype: 'bar',
          categories: this.machinenames && this.machinenames.length > 0 ? this.machinenames : ['-'],
        });
      }
    },

    setTitle(val) {
      this.daterange = this.dateFormat(val);
    },

    dateFormat(val) {
      if (!val) return '';
      try {
        const date = new Date(val);
        if (Number.isNaN(date.getTime())) return '';
        const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
        const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
        const month = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(date);
        return `${day}/${month}/${year}`;
      } catch (e) {
        return '';
      }
    },
  },
};
</script>
